import React from "react";
import T from "prop-types";
import { Map } from "immutable";
import { Link } from "react-router-dom";
import {
  DetailTable,
  Detail,
  CheckForDetail
} from "MODULES/shared/components/DetailTable";
import { translateProps } from "MODULES/shared/decorators";
import pojo from "LIB/pojos";
import withCurrentUser, {
  WithCurrentUser
} from "MODULES/session/decorators/withCurrentUser";
import { withSalesOrder } from "MODULES/SalesOrders/decorators";
import { VENDOR_TERMS } from "MODULES/companies/constants";
import Section from "MODULES/shared/components/Section";
import { CompanyName } from "MODULES/shared/components";
import {
  WAREHOUSE,
  QUALITY_CONTROL,
  ACCOUNTING
} from "MODULES/users/constants";
import NoteTooltip from "MODULES/shared/components/NoteTooltip/NoteTooltip";
import { shortDate } from "LIB/formatters/date";
import { disableIfHasAnyOfRoles, hasAnyOfRoles } from "MODULES/users/helpers";
import {
  COMPANY_ID,
  CONTACT_ID,
  DISABLE_QB_SYNC,
  SHIPPED
} from "APP_ROOT/constants";
import { decorators } from "@orange-marmalade/paginate-this";
import EditDetails from "./EditDetails";
import {
  PICTURES_REQUIRED,
  SEND_SALES_ORDER_ACKNOWLEDGEMENT_LABEL,
  SPECIAL_INSTRUCTIONS
} from "../../../../constants";
import {properCase} from "LIB/formatters/text";

function ParentDetails({ loading, results }) {
  if (loading) return null;
  const itemWithParentExchange = results.find(i => i.parent_exchange_item);
  if (!itemWithParentExchange) return null;

  const parentExchangeItem = itemWithParentExchange.parent_exchange_item;

  return (
    <tr>
      <th className="text-right">Original Sales Order</th>
      <td>
        <Link to={`/sales-orders/${parentExchangeItem.uuid}`}>
          {parentExchangeItem.code}
        </Link>
      </td>
    </tr>
  );
}

const withListId = translateProps(() => ({
  listId: "salesOrders/lineItems"
}));

const ParentDetailsWithResults = withListId(decorators.tabulate(ParentDetails));

export function Details({ salesOrder, currentUser }) {
  const afterHoursText = salesOrder.get("after_hours") ? "Yes" : "No";
  const approvalText = (timestamp, approver) =>
    `Updated by ${approver.get("full_name")} on ${shortDate(timestamp)}`;
  const company = salesOrder.get("company");

  return (
    <Section
      title="Details"
      modal={{
        title: "Edit Sales Order Details",
        content: close => (
          <EditDetails
            initialValues={pojo(salesOrder.toJS()).pick(
              COMPANY_ID,
              CONTACT_ID,
              "credit_terms",
              "cc_email_address",
              "customer_purchase_order_number",
              DISABLE_QB_SYNC,
              "after_hours",
              "third_contact_email",
              "send_sales_order_acknowledgement",
              PICTURES_REQUIRED,
              SPECIAL_INSTRUCTIONS
            )}
            close={close}
          />
        )
      }}
      editable={
        !disableIfHasAnyOfRoles(currentUser, [WAREHOUSE]) &&
        (!salesOrder.get(SHIPPED) ||
          hasAnyOfRoles(currentUser, QUALITY_CONTROL, ACCOUNTING))
      }
    >
      <DetailTable>
        <Detail
          label="Cust. Code"
          value={salesOrder.getIn(["company", "code"])}
        />
        <tr>
          <th className="text-right">Cust. Name</th>
          <td>
            <CompanyName company={salesOrder.toJS().company} />
          </td>
        </tr>
        <Detail
          label="Cust. Phone"
          value={salesOrder.getIn(["company", "phone"])}
        />
        <Detail
          label="Cust. Fax"
          value={salesOrder.getIn(["company", "fax"])}
        />
        <Detail
          label="Cust. Terms"
          value={properCase(salesOrder.get("credit_terms"))}
        />
        <Detail
          label="Blanket Exch. Agreement"
          value={
            <React.Fragment>
              {company.get("blanket_exchange_agreement") &&
                company.get("blanket_exchange_agreement_updated_by") && (
                  <React.Fragment>
                    <span className="text-success">
                      <i className="fa fa-check" /> Approved
                    </span>
                    &nbsp;
                    <span className="text-right">
                      <NoteTooltip
                        placement="right"
                        icon="fa-info-circle"
                        color="rgba(255, 255, 255, 0.25)"
                        note={approvalText(
                          company.get("blanket_exchange_agreement_updated_at"),
                          company.get("blanket_exchange_agreement_updated_by")
                        )}
                      />
                    </span>
                  </React.Fragment>
                )}
            </React.Fragment>
          }
        />
      </DetailTable>
      <hr className="hr-xs hr-dashed" />
      <DetailTable>
        <Detail
          label="Cust. Contact"
          value={salesOrder.getIn(["contact", "name"])}
        />
        <Detail
          label="Contact Email"
          value={[
            <a href={`mailto:${salesOrder.getIn(["contact", "email"])}`}>
              {" "}
              {salesOrder.getIn(["contact", "email"])}{" "}
            </a>
          ]}
        />
        <Detail
          label="Contact Email #2"
          value={[
            <a href={`mailto:${salesOrder.get("cc_email_address")}`}>
              {" "}
              {salesOrder.get("cc_email_address")}{" "}
            </a>
          ]}
        />
        {salesOrder.get("third_contact_email") && (
          <Detail
            label="Contact Email #3"
            value={[
              <a href={`mailto:${salesOrder.get("third_contact_email")}`}>
                {" "}
                {salesOrder.get("third_contact_email")}{" "}
              </a>
            ]}
          />
        )}
        <Detail
          label="Cust. PO #"
          value={salesOrder.get("customer_purchase_order_number")}
        />
        <Detail label="After Hours" value={afterHoursText} />
        <CheckForDetail
          label={SEND_SALES_ORDER_ACKNOWLEDGEMENT_LABEL}
          value={salesOrder.get("send_sales_order_acknowledgement")}
          icon="eye-slash"
        />
        <ParentDetailsWithResults />
      </DetailTable>
    </Section>
  );
}

Details.propTypes = {
  salesOrder: T.instanceOf(Map).isRequired,
  ...WithCurrentUser
};

export default withCurrentUser(withSalesOrder(Details));



// WEBPACK FOOTER //
// ./app/modules/SalesOrders/components/Detail/Panel/Details/Details.jsx